import {
  type FacebookGroupLiveFrame,
  FacebookGroupLiveFrameSchema,
  type FacebookGroupLiveInput,
  type FacebookGroupLiveSession,
  FacebookGroupLiveSessionSchema,
} from '@sbc/domain-models/gateway';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet, apiSend } from './client';

const FRAME_REFRESH_MS = 1_500;

function liveViewPath(connectionId: string): string {
  return `/api/admin/channel-connections/${connectionId}/live-view`;
}

function frameKey(connectionId: string) {
  return ['admin', 'facebook-group', 'live-frame', connectionId] as const;
}

export function useStartLiveView(connectionId: string) {
  return useMutation<FacebookGroupLiveSession>({
    mutationFn: () =>
      apiSend('POST', `${liveViewPath(connectionId)}/start`, {}, FacebookGroupLiveSessionSchema),
  });
}

export function useLiveFrame(connectionId: string, enabled: boolean) {
  return useQuery<FacebookGroupLiveFrame>({
    queryKey: frameKey(connectionId),
    queryFn: () => apiGet(`${liveViewPath(connectionId)}/frame`, FacebookGroupLiveFrameSchema),
    enabled,
    refetchInterval: enabled ? FRAME_REFRESH_MS : false,
    retry: false,
  });
}

export function useSendLiveInput(connectionId: string) {
  const queryClient = useQueryClient();
  return useMutation<FacebookGroupLiveFrame, Error, FacebookGroupLiveInput>({
    mutationFn: (input) =>
      apiSend('POST', `${liveViewPath(connectionId)}/input`, input, FacebookGroupLiveFrameSchema),
    onSuccess: (frame) => queryClient.setQueryData(frameKey(connectionId), frame),
  });
}

export function useStopLiveView(connectionId: string) {
  const queryClient = useQueryClient();
  return useMutation<FacebookGroupLiveSession>({
    mutationFn: () =>
      apiSend('POST', `${liveViewPath(connectionId)}/stop`, {}, FacebookGroupLiveSessionSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'channel-connections'] }),
  });
}
